import { useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Stack,
	Switch,
	Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import RenderInputField from "components/ReusableComponents/RenderInputField/RenderInputField";
import { inputTypes, selectOptionsKeyName } from "data/globalData";
import { catalogActiveStatusColumn } from "./catalogMasterUi";
import { enrichCatalogFormColumns, buildPullerSizeCode } from "./catalogFormHelpers";
import { ZIPPER_PULLER_CATALOGS } from "../ZipperPullerMaster/data";
import { trimActiveStatusDropdowns } from "../TrimsMaster/data";

const EditCatalogRowModal = ({
	open,
	onClose,
	mode = "update",
	catalog,
	columns = [],
	primaryKey,
	editableFields = [],
	creatableFields = [],
	autoPrimaryKey = false,
	selectedRow,
	formOptions = {},
	onSave,
}) => {
	const [pageData, setPageData] = useState({});
	const [errorObj, setErrorObj] = useState({});
	const isCreate = mode === "create";
	const activeFields = isCreate ? creatableFields : editableFields;

	const resolvedColumns = useMemo(
		() => enrichCatalogFormColumns(catalog, columns, formOptions, mode),
		[catalog, columns, formOptions, mode],
	);

	const activeMetalFinishNames = useMemo(
		() => new Set((formOptions.metal_finishes ?? []).map((finish) => finish.metal_finish_name)),
		[formOptions.metal_finishes],
	);

	useEffect(() => {
		if (!open) return;
		if (isCreate) {
			setPageData({ [catalogActiveStatusColumn.key]: trimActiveStatusDropdowns.ACTIVE.key });
			setErrorObj({});
			return;
		}
		if (selectedRow) {
			const nextRow = { ...selectedRow };
			if (
				catalog === ZIPPER_PULLER_CATALOGS.PULLER_SIZES &&
				nextRow.default_metal_finish &&
				!activeMetalFinishNames.has(nextRow.default_metal_finish)
			) {
				nextRow.default_metal_finish = "";
			}
			setPageData(nextRow);
			setErrorObj({});
		}
	}, [open, selectedRow, isCreate, catalog, activeMetalFinishNames]);

	useEffect(() => {
		if (!open || !isCreate || catalog !== ZIPPER_PULLER_CATALOGS.PULLER_SIZES) return;
		if (!pageData.puller_code || !pageData.size) return;
		const nextPullerSizeCode = buildPullerSizeCode(pageData.puller_code, pageData.size);
		if (pageData.puller_size_code === nextPullerSizeCode) return;
		setPageData((prev) => ({ ...prev, puller_size_code: nextPullerSizeCode }));
	}, [open, isCreate, catalog, pageData.puller_code, pageData.size, pageData.puller_size_code]);

	const formColumns = useMemo(
		() =>
			resolvedColumns.filter((column) => {
				if (!activeFields.includes(column.key)) return false;
				if (column.key === catalogActiveStatusColumn.key) return false;
				if (isCreate && autoPrimaryKey && column.key === primaryKey) return false;
				if (!isCreate && column.key === primaryKey) return false;
				return true;
			}),
		[resolvedColumns, activeFields, isCreate, autoPrimaryKey, primaryKey],
	);

	const handleClose = () => {
		setErrorObj({});
		onClose?.();
	};

	const handleSave = async () => {
		const nextErrors = {};
		formColumns.forEach((column) => {
			if (column.constraints?.NOTNULL && !pageData?.[column.key]) {
				nextErrors[column.key] = `${column.name} is required`;
			}
		});
		if (Object.keys(nextErrors).length) {
			setErrorObj(nextErrors);
			return;
		}
		await onSave?.(pageData);
	};

	const activeValue = pageData?.[catalogActiveStatusColumn.key] || trimActiveStatusDropdowns.INACTIVE.key;
	const primaryKeyColumn = resolvedColumns.find((column) => column.key === primaryKey);

	return (
		<Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
			<DialogTitle>{isCreate ? "Add Catalog Row" : "Edit Catalog Row"}</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{ pt: 1 }}>
					<Typography variant="body2" color="text.secondary">
						Catalog: <strong>{catalog}</strong>
					</Typography>
					{isCreate && autoPrimaryKey ? (
						<Typography variant="body2" color="text.secondary">
							{primaryKeyColumn?.name || primaryKey} will be generated automatically on save.
						</Typography>
					) : null}
					{!isCreate && primaryKeyColumn ? (
						<RenderInputField
							column={{
								...primaryKeyColumn,
								name: primaryKeyColumn.name || primaryKey,
								input_type: inputTypes.ENTER.key,
							}}
							data={pageData}
							setData={setPageData}
							width="100%"
							disabled
						/>
					) : null}
					<Box display="flex" flexWrap="wrap" gap="3%">
						{formColumns.map((column) => (
							<RenderInputField
								key={column.key}
								column={column}
								data={pageData}
								setData={setPageData}
								selectOptObjName={selectOptionsKeyName}
								width="30%"
								error={errorObj[column.key]}
							/>
						))}
					</Box>
					{isCreate && catalog === ZIPPER_PULLER_CATALOGS.PULLER_SIZES && pageData.puller_code && pageData.size ? (
						<Typography variant="body2" color="text.secondary">
							Puller Size Code: <strong>{buildPullerSizeCode(pageData.puller_code, pageData.size)}</strong>
						</Typography>
					) : null}
					<Box
						sx={{
							border: `1px solid ${grey[200]}`,
							borderRadius: 2,
							px: 2,
							py: 1.5,
							bgcolor: "#fcfcfd",
						}}>
						<FormControlLabel
							control={
								<Switch
									checked={activeValue === trimActiveStatusDropdowns.ACTIVE.key}
									onChange={(event) =>
										setPageData((prev) => ({
											...prev,
											[catalogActiveStatusColumn.key]: event.target.checked
												? trimActiveStatusDropdowns.ACTIVE.key
												: trimActiveStatusDropdowns.INACTIVE.key,
										}))
									}
								/>
							}
							label={`Active Status: ${activeValue}`}
						/>
					</Box>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose}>Cancel</Button>
				<Button variant="contained" onClick={handleSave}>
					{isCreate ? "Create" : "Save"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default EditCatalogRowModal;
